/**
 * 统一日志工具
 * 提供格式化的日志输出，支持开关控制
 */

// 日志级别
const LOG_LEVELS = {
  DEBUG: 0,
  INFO: 1,
  WARN: 2,
  ERROR: 3,
  NONE: 4
}

// 日志级别名称
const LEVEL_NAMES = {
  [LOG_LEVELS.DEBUG]: 'DEBUG',
  [LOG_LEVELS.INFO]: 'INFO',
  [LOG_LEVELS.WARN]: 'WARN',
  [LOG_LEVELS.ERROR]: 'ERROR'
}

// 默认配置
const defaultConfig = {
  enabled: import.meta.env.VITE_APP_LOG_ENABLED ? import.meta.env.VITE_APP_LOG_ENABLED === 'true' : false, // 总开关（默认关闭）
  level: LOG_LEVELS.DEBUG, // 默认显示所有级别
  format: '[{time}] [{level}] [{module}] [{file}:{line}] {message}', // 日志格式
  showTimestamp: true, // 是否显示时间戳
  showLevel: true, // 是否显示级别
  showModule: true, // 是否显示模块名
  showFile: true // 是否显示文件名和行号
}

// 从localStorage读取配置
const getConfig = () => {
  try {
    const stored = localStorage.getItem('loggerConfig')
    if (stored) {
      const config = JSON.parse(stored)
      return { ...defaultConfig, ...config }
    }
  } catch (error) {
    console.error('读取日志配置失败:', error)
  }
  return defaultConfig
}

// 保存配置到localStorage
const saveConfig = (config) => {
  try {
    localStorage.setItem('loggerConfig', JSON.stringify(config))
  } catch (error) {
    console.error('保存日志配置失败:', error)
  }
}

// 格式化时间戳
const formatTime = () => {
  const now = new Date()
  const year = now.getFullYear()
  const month = String(now.getMonth() + 1).padStart(2, '0')
  const day = String(now.getDate()).padStart(2, '0')
  const hours = String(now.getHours()).padStart(2, '0')
  const minutes = String(now.getMinutes()).padStart(2, '0')
  const seconds = String(now.getSeconds()).padStart(2, '0')
  const milliseconds = String(now.getMilliseconds()).padStart(3, '0')
  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}.${milliseconds}`
}

// 获取调用栈信息，返回文件名和行号
const getCallerInfo = () => {
  try {
    const stack = new Error().stack
    if (!stack) {
      return { file: 'unknown', line: '0' }
    }
    
    // 解析调用栈
    // 格式通常是: "at functionName (file:///path/to/file.js:line:column)"
    // 或者: "at file:///path/to/file.js:line:column"
    const stackLines = stack.split('\n')
    
    // 跳过前几行（Error 本身、getCallerInfo、formatMessage、logger 方法）
    // 找到第一个不是 logger.js 的调用
    for (let i = 0; i < stackLines.length; i++) {
      const line = stackLines[i]
      // 跳过 logger.js 相关的调用
      if (line.includes('logger.js') || line.includes('getCallerInfo') || line.includes('formatMessage')) {
        continue
      }
      
      // 匹配文件路径和行号
      // 匹配格式: "at ... (file:///path/to/file.js:line:column)" 或 "at file:///path/to/file.js:line:column"
      const match = line.match(/\(?(.+?):(\d+):(\d+)\)?$/)
      if (match) {
        const fullPath = match[1]
        const lineNumber = match[2]
        
        // 提取文件名（只显示文件名，不显示完整路径）
        let fileName = fullPath.split('/').pop() || fullPath.split('\\').pop() || 'unknown'
        
        // 去掉查询参数（如 ?t=1766659556337）
        if (fileName.includes('?')) {
          fileName = fileName.split('?')[0]
        }
        
        return { file: fileName, line: lineNumber }
      }
    }
    
    return { file: 'unknown', line: '0' }
  } catch (error) {
    return { file: 'unknown', line: '0' }
  }
}

// 格式化消息
const formatMessage = (level, module, message, ...args) => {
  const config = getConfig()
  
  if (!config.enabled || level < config.level) {
    return null
  }
  
  // 获取调用者信息（文件名和行号）
  const callerInfo = config.showFile ? getCallerInfo() : { file: '', line: '' }
  
  // 构建带样式的格式化消息
  const parts = []
  const styles = []
  
  // 时间戳样式（灰色）
  if (config.showTimestamp) {
    const time = formatTime()
    parts.push(`%c[${time}]`)
    styles.push('color: #999')
  }
  
  // 级别样式（不同级别不同颜色）
  if (config.showLevel) {
    const levelName = LEVEL_NAMES[level]
    parts.push(`%c[${levelName}]`)
    const levelColors = {
      [LOG_LEVELS.DEBUG]: 'color: #666',
      [LOG_LEVELS.INFO]: 'color: #2196F3',
      [LOG_LEVELS.WARN]: 'color: #FF9800',
      [LOG_LEVELS.ERROR]: 'color: #F44336'
    }
    styles.push(levelColors[level] || 'color: #333')
  }
  
  // 模块样式（灰色）
  if (config.showModule && module) {
    parts.push(`%c[${module}]`)
    styles.push('color: #999')
  }
  
  // 文件位置样式（灰色）
  if (config.showFile && callerInfo.file) {
    parts.push(`%c[${callerInfo.file}:${callerInfo.line}]`)
    styles.push('color: #999')
  }
  
  // 消息内容（黑色）
  let messageStr = ''
  if (typeof message === 'string') {
    messageStr = message
  } else if (message !== null && message !== undefined) {
    messageStr = String(message)
  }
  parts.push(`%c${messageStr}`)
  styles.push('color: #333')
  
  const formattedMessage = parts.join(' ')
  
  // 返回格式化消息、样式和原始参数
  return { 
    formattedMessage, 
    styles,
    originalArgs: args.length > 0 ? args : []
  }
}

// 创建日志函数
const createLogger = (module = '') => {
  return {
    /**
     * 调试日志
     */
    debug: (message, ...args) => {
      const result = formatMessage(LOG_LEVELS.DEBUG, module, message, ...args)
      if (result) {
        console.log(result.formattedMessage, ...result.styles, ...result.originalArgs)
      }
    },
    
    /**
     * 信息日志
     */
    info: (message, ...args) => {
      const result = formatMessage(LOG_LEVELS.INFO, module, message, ...args)
      if (result) {
        console.log(result.formattedMessage, ...result.styles, ...result.originalArgs)
      }
    },
    
    /**
     * 警告日志
     */
    warn: (message, ...args) => {
      const result = formatMessage(LOG_LEVELS.WARN, module, message, ...args)
      if (result) {
        console.warn(result.formattedMessage, ...result.styles, ...result.originalArgs)
      }
    },
    
    /**
     * 错误日志
     */
    error: (message, ...args) => {
      const result = formatMessage(LOG_LEVELS.ERROR, module, message, ...args)
      if (result) {
        console.error(result.formattedMessage, ...result.styles, ...result.originalArgs)
      }
    },
    
    /**
     * 设置日志级别
     */
    setLevel: (level) => {
      const config = getConfig()
      config.level = level
      saveConfig(config)
    },
    
    /**
     * 启用/禁用日志
     */
    setEnabled: (enabled) => {
      const config = getConfig()
      config.enabled = enabled
      saveConfig(config)
    },
    
    /**
     * 获取当前配置
     */
    getConfig: () => {
      return getConfig()
    },
    
    /**
     * 更新配置
     */
    updateConfig: (newConfig) => {
      const config = getConfig()
      const updated = { ...config, ...newConfig }
      saveConfig(updated)
    }
  }
}

// 默认导出（全局日志器）
const logger = createLogger()

// 导出
export default logger
export { createLogger, LOG_LEVELS }

